import { BannerType } from '@enums/banner-type.enum'
import { CheckCircleIcon, ExclamationCircleIcon, ExclamationTriangleIcon, InformationCircleIcon, XMarkIcon } from '@heroicons/react/20/solid'
import { ReactNode } from 'react'
import { useSubscriptionBanner } from '../../contexts/subscription-banner.context'

export interface BannerProps {
  message?: ReactNode
  children?: ReactNode
  type?: BannerType
  className?: string
  show?: boolean
  onClose?: () => void
}

function getColorScheme(type: BannerType) {
  switch (type) {
    case BannerType.SUCCESS:
      return {
        bgColor: 'bg-green-50',
        iconColor: 'text-green-400',
        textColor: 'text-green-800',
        buttonBgColor: 'bg-green-50',
        buttonTextColor: 'text-green-500',
        buttonHoverBgColor: 'bg-green-100',
        focusRingColor: 'focus:ring-green-600',
        focusRingOffsetColor: 'focus:ring-offset-green-50',
      }
    case BannerType.ERROR:
      return {
        bgColor: 'bg-red-50',
        iconColor: 'text-red-400',
        textColor: 'text-red-800',
        buttonBgColor: 'bg-red-50',
        buttonTextColor: 'text-red-500',
        buttonHoverBgColor: 'bg-red-100',
        focusRingColor: 'focus:ring-red-600',
        focusRingOffsetColor: 'focus:ring-offset-red-50',
      }
    case BannerType.WARN:
      return {
        bgColor: 'bg-yellow-50',
        iconColor: 'text-yellow-400',
        textColor: 'text-yellow-800',
        buttonBgColor: 'bg-yellow-50',
        buttonTextColor: 'text-yellow-500',
        buttonHoverBgColor: 'bg-yellow-100',
        focusRingColor: 'focus:ring-yellow-600',
        focusRingOffsetColor: 'focus:ring-offset-yellow-50',
      }
    case BannerType.INFO:
    default:
      return {
        bgColor: 'bg-[#c3c1ff]',
        iconColor: 'text-blue',
        textColor: 'text-blue',
        buttonBgColor: 'bg-[#c3c1ff]',
        buttonTextColor: 'text-blue',
        buttonHoverBgColor: 'bg-blue-300',
        focusRingColor: 'focus:ring-blue',
        focusRingOffsetColor: 'focus:ring-offset-blue',
      }
  }
}

const getIcon = (type: BannerType) => {
  switch (type) {
    case BannerType.SUCCESS:
      return CheckCircleIcon
    case BannerType.ERROR:
      return ExclamationCircleIcon
    case BannerType.WARN:
      return ExclamationTriangleIcon
    case BannerType.INFO:
    default:
      return InformationCircleIcon
  }
}

export default function Banner({ message, children, type = BannerType.INFO, className, show, onClose }: BannerProps) {
  const subscriptionBanner = useSubscriptionBanner()
  const colors = getColorScheme(type)
  const Icon = getIcon(type)

  // Determine if banner should be shown based on props or context
  const shouldShow = show !== undefined ? show : subscriptionBanner?.bannerDisplay

  // Determine close handler based on props or context
  const handleClose = onClose || (() => subscriptionBanner?.setBannerDisplay(false))

  if (!shouldShow) {
    return null
  }

  return (
    <div className={`${colors.bgColor} z-50 rounded-md p-4 ${className || ''}`}>
      <div className="flex items-center justify-center">
        <div className="shrink-0">
          <Icon className={`size-5 ${colors.iconColor}`} aria-hidden="true" />
        </div>
        <div className="ml-3">
          <p className={`text-sm font-medium ${colors.textColor}`}>{message || children}</p>
        </div>
        <div className="ml-auto pl-3">
          <div className="-m-1.5">
            <button
              type="button"
              className={`inline-flex rounded-md ${colors.buttonBgColor} p-1.5 ${colors.buttonTextColor} hover:${colors.buttonHoverBgColor} focus:outline-none ${colors.focusRingColor} focus:ring-2 ${colors.focusRingOffsetColor}`}
              onClick={handleClose}
            >
              <span className="sr-only">Dismiss</span>
              <XMarkIcon className="size-5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
